import React, { useState } from "react";
import { Container, Button, Form, Row, Col, Card } from "react-bootstrap";
import {
  Formik,
  Field,
  Form as FormikForm,
  ErrorMessage,
  FieldArray,
} from "formik";
import { withTranslation } from "react-i18next";
import * as Yup from "yup";
import callApi from "../../helpers/CallApi";
import {
  createDistributor,
  getDistributor,
  updateDistributor,
} from "../../helpers/API";
import useApi from "../../Hooks/useApi";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

const Add = ({ t }) => {
  const { operation, id } = useParams();

  // Dynamic validation schema using translations
  const getValidationSchema = () => {
    return Yup.object().shape({
      companyName: Yup.string().required(
        t("distributor.add.validation.companyNameRequired")
      ),
      email: Yup.string()
        .email(t("distributor.add.validation.emailInvalid"))
        .required(t("distributor.add.validation.emailRequired")),
      mobile: Yup.string()
        .matches(/^[0-9]{10}$/, t("distributor.add.validation.mobileInvalid"))
        .required(t("distributor.add.validation.mobileRequired")),
      gstNumber: Yup.string().required(
        t("distributor.add.validation.gstRequired")
      ),
      panCard: Yup.string().required(
        t("distributor.add.validation.panRequired")
      ),
      subscriptionPeriod: Yup.string().required(
        t("distributor.add.validation.subscriptionPeriodRequired")
      ),
      subscriptionId: Yup.string().required(
        t("distributor.add.validation.subscriptionRequired")
      ),
      distributors: Yup.array().of(
        Yup.object().shape({
          name: Yup.string().required(
            t("distributor.add.validation.nameRequired")
          ),
          email: Yup.string()
            .email(t("distributor.add.validation.emailInvalid"))
            .required(t("distributor.add.validation.emailRequired")),
          mobile: Yup.string()
            .matches(
              /^[0-9]{10}$/,
              t("distributor.add.validation.mobileInvalid")
            )
            .required(t("distributor.add.validation.mobileRequired")),
          role: Yup.string().required(
            t("distributor.add.validation.roleRequired")
          ),
        })
      ),
    });
  };

  const initialValues = {
    companyName: "",
    email: "",
    mobile: "",
    gstNumber: "",
    panCard: "",
    logo: null,
    subscriptionPeriod: "",
    subscriptionId: "",
    distributors: [],
  };

  const [formData, setFormData] = useState(initialValues);
  const [disabled, setDisabled] = useState(operation === "view");
  const navigate = useNavigate();

  useApi({
    callback: getDistributor,
    payload: id,
    dependencies: [id],
    shouldCallApi: Boolean(id),
    onSuccess: (res) => {
      const {
        companyName,
        email,
        mobile,
        gstNumber,
        panCard,
        logo,
        subscriptionId,
        distributors,
      } = res;
      setFormData({
        companyName,
        email,
        mobile,
        gstNumber,
        panCard,
        logo,
        subscriptionId: String(subscriptionId),
        distributors: distributors?.map((distributor) => ({
          name: distributor.name,
          email: distributor.email,
          mobile: distributor.mobile,
          role: distributor.role,
        })),
      });
    },
  });

  const handleSubmit = (values) => {
    const {
      companyName,
      email,
      mobile,
      gstNumber,
      panCard,
      logo,
      subscriptionId,
      distributors,
    } = values;

    const payload = {
      companyName,
      email,
      mobile,
      gstNumber,
      panCard,
      logo,
      subscriptionId,
      distributors,
    };

    callApi({
      callback: !id ? createDistributor : updateDistributor,
      payload: id ? [id, payload] : [payload],
      onSuccess: ({ message }) => {
        toast.success(message);
        navigate("/distributor");
      },
      onError: () => {
        toast.error(t("distributor.add.messages.error"));
      },
    });
  };

  const getHeading = () => {
    return t(`distributor.add.headings.${operation}`);
  };

  return (
    <div className="page-content">
      <Container className="py-5">
        <Card className="p-4 shadow">
          <h3 className="mb-4 text-center">{getHeading()}</h3>

          <Formik
            initialValues={formData}
            enableReinitialize
            validationSchema={getValidationSchema()}
            onSubmit={handleSubmit}
            validateOnBlur
            validateOnChange
          >
            {({ setFieldValue, values, errors, touched }) => (
              <FormikForm>
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="required">
                        {t("distributor.add.labels.companyName")}
                      </Form.Label>
                      <Field
                        name="companyName"
                        disabled={disabled}
                        className={`form-control ${
                          touched.companyName && errors.companyName
                            ? "is-invalid"
                            : ""
                        }`}
                        placeholder={t(
                          "distributor.add.placeholders.enterCompanyName"
                        )}
                      />
                      <ErrorMessage
                        name="companyName"
                        component="div"
                        className="invalid-feedback"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="required">
                        {t("distributor.add.labels.email")}
                      </Form.Label>
                      <Field
                        name="email"
                        type="email"
                        disabled={disabled}
                        className={`form-control ${
                          touched.email && errors.email ? "is-invalid" : ""
                        }`}
                        placeholder={t(
                          "distributor.add.placeholders.enterEmail"
                        )}
                      />
                      <ErrorMessage
                        name="email"
                        component="div"
                        className="invalid-feedback"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="required">
                        {t("distributor.add.labels.mobileNumber")}
                      </Form.Label>
                      <Field
                        name="mobile"
                        disabled={disabled}
                        className={`form-control ${
                          touched.mobile && errors.mobile ? "is-invalid" : ""
                        }`}
                        placeholder={t(
                          "distributor.add.placeholders.enterMobile"
                        )}
                      />
                      <ErrorMessage
                        name="mobile"
                        component="div"
                        className="invalid-feedback"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="required">
                        {t("distributor.add.labels.gstNumber")}
                      </Form.Label>
                      <Field
                        name="gstNumber"
                        disabled={disabled}
                        className={`form-control ${
                          touched.gstNumber && errors.gstNumber
                            ? "is-invalid"
                            : ""
                        }`}
                        placeholder={t(
                          "distributor.add.placeholders.enterGstNumber"
                        )}
                      />
                      <ErrorMessage
                        name="gstNumber"
                        component="div"
                        className="invalid-feedback"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="required">
                        {t("distributor.add.labels.panCardNumber")}
                      </Form.Label>
                      <Field
                        name="panCard"
                        disabled={disabled}
                        className={`form-control ${
                          touched.panCard && errors.panCard ? "is-invalid" : ""
                        }`}
                        placeholder={t(
                          "distributor.add.placeholders.enterPanCard"
                        )}
                      />
                      <ErrorMessage
                        name="panCard"
                        component="div"
                        className="invalid-feedback"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>
                        {t("distributor.add.labels.companyLogo")}
                      </Form.Label>
                      <Form.Control
                        type="file"
                        disabled={disabled}
                        onChange={(event) =>
                          setFieldValue("logo", event.currentTarget.files[0])
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="required">
                        {t("distributor.add.labels.subscriptionPeriod")}
                      </Form.Label>
                      <Field
                        as="select"
                        name="subscriptionPeriod"
                        disabled={disabled}
                        className={`form-control ${
                          touched.subscriptionPeriod &&
                          errors.subscriptionPeriod
                            ? "is-invalid"
                            : ""
                        }`}
                      >
                        <option value="">
                          {t("distributor.add.options.selectPeriod")}
                        </option>
                        <option value="monthly">
                          {t("distributor.add.options.monthly")}
                        </option>
                        <option value="yearly">
                          {t("distributor.add.options.yearly")}
                        </option>
                      </Field>
                      <ErrorMessage
                        name="subscriptionPeriod"
                        component="div"
                        className="invalid-feedback"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="required">
                        {t("distributor.add.labels.subscription")}
                      </Form.Label>
                      <Field
                        as="select"
                        name="subscriptionId"
                        disabled={disabled}
                        className={`form-control ${
                          touched.subscriptionId && errors.subscriptionId
                            ? "is-invalid"
                            : ""
                        }`}
                      >
                        <option value="">
                          {t("distributor.add.options.selectSubscription")}
                        </option>
                        <option value="1">
                          {t("distributor.add.options.basic")}
                        </option>
                        <option value="2">
                          {t("distributor.add.options.premium")}
                        </option>
                      </Field>
                      <ErrorMessage
                        name="subscription"
                        component="div"
                        className="invalid-feedback"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <hr />
                <h5 className="mb-3">
                  {t("distributor.add.headings.distributorUserInfo")}
                </h5>

                <FieldArray name="distributors">
                  {({ push, remove }) => {
                    const roleAsDistributor = values?.distributors?.find(
                      (distributor) => distributor.role === "distributor"
                    );

                    return (
                      <>
                        {values.distributors.map((distributor, index) => (
                          <Row className="mb-3" key={index}>
                            <Col md={3}>
                              <Form.Group>
                                <Form.Label className="required">
                                  {t("distributor.add.labels.name")}
                                </Form.Label>
                                <Field
                                  disabled={disabled}
                                  name={`distributors[${index}].name`}
                                  className={`form-control ${
                                    touched?.distributors?.[index]?.name &&
                                    errors?.distributors?.[index]?.name
                                      ? "is-invalid"
                                      : ""
                                  }`}
                                  placeholder={t(
                                    "distributor.add.placeholders.distributorName"
                                  )}
                                />
                                <ErrorMessage
                                  name={`distributors[${index}].name`}
                                  component="div"
                                  className="invalid-feedback"
                                />
                              </Form.Group>
                            </Col>
                            <Col md={3}>
                              <Form.Group>
                                <Form.Label className="required">
                                  {t("distributor.add.labels.email")}
                                </Form.Label>
                                <Field
                                  name={`distributors[${index}].email`}
                                  type="email"
                                  disabled={disabled}
                                  className={`form-control ${
                                    touched?.distributors?.[index]?.email &&
                                    errors?.distributors?.[index]?.email
                                      ? "is-invalid"
                                      : ""
                                  }`}
                                  placeholder={t(
                                    "distributor.add.placeholders.distributorEmail"
                                  )}
                                />
                                <ErrorMessage
                                  name={`distributors[${index}].email`}
                                  component="div"
                                  className="invalid-feedback"
                                />
                              </Form.Group>
                            </Col>
                            <Col md={3}>
                              <Form.Group>
                                <Form.Label className="required">
                                  {t("distributor.add.labels.mobile")}
                                </Form.Label>
                                <Field
                                  name={`distributors[${index}].mobile`}
                                  disabled={disabled}
                                  className={`form-control ${
                                    touched?.distributors?.[index]?.mobile &&
                                    errors?.distributors?.[index]?.mobile
                                      ? "is-invalid"
                                      : ""
                                  }`}
                                  placeholder={t(
                                    "distributor.add.placeholders.distributorMobile"
                                  )}
                                />
                                <ErrorMessage
                                  name={`distributors[${index}].mobile`}
                                  component="div"
                                  className="invalid-feedback"
                                />
                              </Form.Group>
                            </Col>
                            <Col md={2}>
                              <Form.Group>
                                <Form.Label className="required">
                                  {t("distributor.add.labels.role")}
                                </Form.Label>
                                <Field
                                  as="select"
                                  disabled={disabled}
                                  name={`distributors[${index}].role`}
                                  className={`form-control ${
                                    touched?.distributors?.[index]?.role &&
                                    errors?.distributors?.[index]?.role
                                      ? "is-invalid"
                                      : ""
                                  }`}
                                >
                                  <option value="">
                                    {t("distributor.add.options.select")}
                                  </option>
                                  <option
                                    value="distributor"
                                    disabled={
                                      roleAsDistributor?.role === "distributor"
                                    }
                                  >
                                    {t("distributor.add.options.distributor")}
                                  </option>
                                  <option value="sub-distributor">
                                    {t(
                                      "distributor.add.options.subDistributor"
                                    )}
                                  </option>
                                </Field>
                                <ErrorMessage
                                  name={`distributors[${index}].role`}
                                  component="div"
                                  className="invalid-feedback"
                                />
                              </Form.Group>
                            </Col>
                            {!disabled && (
                              <Col md={1} className="d-flex align-items-end">
                                <Button
                                  variant="danger"
                                  type="button"
                                  onClick={() => remove(index)}
                                >
                                  {t(
                                    "distributor.add.buttons.removeDistributor"
                                  )}
                                </Button>
                              </Col>
                            )}
                          </Row>
                        ))}
                        {!disabled && (
                          <div className="mb-3">
                            <Button
                              variant="secondary"
                              type="button"
                              onClick={() =>
                                push({
                                  role: "",
                                  name: "",
                                  email: "",
                                  mobile: "",
                                })
                              }
                            >
                              {t("distributor.add.buttons.addDistributor")}
                            </Button>
                          </div>
                        )}
                      </>
                    );
                  }}
                </FieldArray>

                {!disabled && (
                  <div className="text-end mt-4">
                    <Button variant="primary" type="submit">
                      {t("distributor.add.buttons.saveDistributor")}
                    </Button>
                  </div>
                )}
              </FormikForm>
            )}
          </Formik>
        </Card>
      </Container>
    </div>
  );
};

export default withTranslation()(Add);
